import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import useSWR from 'swr';
import { formatPhoneNumberWithoutPrefix } from '@/common/utils/phoneNumber';
import { useIdentity } from '$api-clients/hooks';
import type { NumericMailingAddress } from '../../types';
import { toMailingAddress } from '../../utils/address';
import { mailingAddressHasValidGeolocation } from '../../utils/location';
import { fetchOrganization } from '../useGetTeamOrganization/helpers';

export type PartnerLeadAgent = {
  id: string;
  fullName: string;
  legacyExternalId: string | null;
};

export type PartnerDetails = {
  email: string | null | undefined;
  id: string;
  legalName: string;
  alias: string | null | undefined;
  websiteUrl: string | null;
  phoneNumber: string | null;
  description: string | null | undefined;
  logoUri: string | null | undefined;
  formattedAddress: string | null;
  leadAgents: PartnerLeadAgent[];
  mailingAddress: NumericMailingAddress | undefined;
  topLevelTeamId: string | undefined;
};

export const useGetPartnerInformation = ({
  teamId,
}: {
  teamId?: string | null;
}): { partnerDetails?: PartnerDetails } => {
  const { data: team } = useIdentity(
    '/teams/{teamId}',
    teamId ? { params: { path: { teamId } } } : null,
    {
      onError: () => toast('error', 'Please refresh and try again'),
    },
  );

  const { data: members } = useIdentity(
    '/teams/{teamId}/members',
    teamId ? { params: { path: { teamId } } } : null,
    {
      onError: (error) =>
        datadogRum.addError('Failed to load partner lead agents.', {
          error,
          teamId,
        }),
    },
  );

  // The org walk runs through SWR so its result is cached, revalidated, and
  // retried on failure across team selections.
  const { data: orgTeams, error: orgError } = useSWR(
    teamId ? ['partner-org', teamId] : null,
    ([, id]) => fetchOrganization(id),
    {
      revalidateOnFocus: false,
      onError: (error) =>
        datadogRum.addError('Failed to resolve partner organization.', {
          error,
          teamId,
        }),
    },
  );

  // A failed org walk degrades gracefully: render the partner without the
  // multi-location affordance rather than blocking the whole panel.
  const resolvedOrgTeams = orgError ? [] : orgTeams;

  if (!team || !members || resolvedOrgTeams === undefined) {
    return {};
  }

  const fullAddress = toMailingAddress(team.mailingAddress);
  const mailingAddress: NumericMailingAddress | undefined =
    fullAddress && mailingAddressHasValidGeolocation(fullAddress)
      ? {
          ...fullAddress,
          geolocationLatitude: Number(fullAddress.geolocationLatitude),
          geolocationLongitude: Number(fullAddress.geolocationLongitude),
        }
      : undefined;

  const leadAgents: PartnerLeadAgent[] = (members.data ?? [])
    .filter(
      (member) => member.roles.includes('LEAD') && member.status === 'ACTIVE',
    )
    .map((member) => ({
      id: member.userId,
      fullName: `${member.firstName} ${member.lastName}`.trim(),
      legacyExternalId: member.legacyExternalId ?? null,
    }));

  const activeOrgTeams = resolvedOrgTeams.filter(
    (orgTeam) => orgTeam.status === 'ACTIVE',
  );
  const topLevelTeamId =
    activeOrgTeams.length > 1
      ? activeOrgTeams.find((orgTeam) => !orgTeam.parentTeamId)?.id
      : undefined;

  return {
    partnerDetails: {
      email: team.email,
      id: team.id,
      legalName: team.legalName ?? '',
      alias: team.legalName === team.alias ? undefined : team.alias,
      websiteUrl: team.websiteUrl,
      phoneNumber: formatPhoneNumberWithoutPrefix(team.phoneNumber ?? null),
      description: team.description,
      logoUri: team.logoUri ?? team.logoHorizontalUri,
      formattedAddress: fullAddress?.formattedAddress ?? null,
      leadAgents,
      mailingAddress,
      topLevelTeamId,
    },
  };
};
